/**
 * minutasV2Service.js — cliente de la API v2
 *
 * Un único punto de contacto con el backend v2. En v1 la URL de la API y el
 * manejo de errores estaban repetidos en decenas de puntos dentro de
 * ClipchampViewer.js; aquí viven una sola vez.
 *
 * Contrato central:  generarMinuta({ transcripcion, formato })  →  { html, verificacion }
 */

const API = (process.env.REACT_APP_API_ENDPOINT || 'http://localhost:7071/api').replace(/\/+$/, '');

/** Error de la API v2, con el `code` estable que devuelve el backend. */
export class MinutasApiError extends Error {
  constructor(mensaje, { code = 'UNKNOWN', status = null, providerStatus = null } = {}) {
    super(mensaje);
    this.name = 'MinutasApiError';
    this.code = code;
    this.status = status;
    this.providerStatus = providerStatus;
  }

  /** Mensaje pensado para mostrarle a una persona, no para depurar. */
  get mensajeUsuario() {
    switch (this.code) {
      case 'LLM_NOT_CONFIGURED':
        return 'El motor de IA no está configurado. Falta la clave de Azure OpenAI en el backend.';
      case 'EMPTY_TRANSCRIPT':
        return 'La transcripción está vacía: no hay contenido para generar la minuta.';
      case 'FORMAT_TOO_LARGE':
        return 'El formato .docx supera el límite de 10 MB.';
      case 'FORMAT_UNREADABLE':
        return 'No se pudo leer el archivo .docx. Verifica que no esté dañado ni protegido.';
      case 'LLM_REQUEST_FAILED':
        return 'El servicio de IA no respondió correctamente. Inténtalo de nuevo en un momento.';
      case 'DB_CONNECTION_FAILED':
      case 'DB_NOT_CONFIGURED':
        return 'No hay conexión con la base de datos, así que los formatos guardados no están disponibles. ' +
               'Puedes seguir subiendo un .docx y generar con él.';
      case 'FORMATS_TABLE_MISSING':
        return 'Falta preparar la base de datos para los formatos guardados. ' +
               'Mientras tanto puedes subir un .docx y generar con él.';
      case 'MISSING_USER':
        return 'No se identificó al usuario. Vuelve a iniciar sesión.';
      case 'SIN_IDENTIDAD':
      case 'SESION_CADUCADA':
        return 'Tu sesión caducó o no se pudo comprobar. Vuelve a entrar a la aplicación.';
      case 'OTRA_ORGANIZACION':
        return 'Esa cuenta no es de esta organización.';
      case 'SIN_CONFIGURACION':
        return 'El servidor no puede comprobar quién eres ahora mismo. Avisa a quien lo administre.';
      default:
        return this.message;
    }
  }
}

/**
 * De dónde sale el token que acredita quién eres ante la API.
 *
 * ── Por qué una ranura y no un `import` ──────────────────────────────────
 *
 * Porque el token lo tiene MSAL, que vive en el árbol de React, y este módulo
 * no es un componente: lo llaman servicios, utilidades y pantallas. Pasarlo por
 * parámetro habría obligado a enhebrarlo por las cuarenta funciones de abajo,
 * la mayoría de las cuales no tienen nada que ver con permisos.
 *
 * Lo pone `AuthenticatedApp` una vez, al arrancar.
 *
 * ── Y por qué es opcional ────────────────────────────────────────────────
 *
 * Porque si pedir el token falla, lo que NO puede pasar es que deje de
 * funcionar la aplicación entera. Sin token, las rutas que comprueban permisos
 * contestan un 401 que se entiende —«vuelve a entrar»— y las demás siguen como
 * siempre. Hacer que una pantalla de actas se caiga porque no se pudo renovar
 * una sesión para una cosa que esa pantalla no usa sería cambiar un problema
 * pequeño por uno grande.
 */
let darToken = null;

/** La pone la aplicación al arrancar. Recibe una función que devuelve el token. */
export function usarTokenDeSesion(proveedor) {
  darToken = typeof proveedor === 'function' ? proveedor : null;
}

async function cabeceraDeSesion() {
  if (!darToken) return {};
  try {
    const token = await darToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function peticion(ruta, opciones = {}) {
  let respuesta;
  const sesion = await cabeceraDeSesion();
  try {
    respuesta = await fetch(`${API}/v2/${ruta}`, {
      ...opciones,
      // Las cabeceras van DESPUES de esparcir las opciones, no antes: con
      // `...opciones` detras, unas cabeceras propias de la llamada borraban
      // estas enteras y la peticion salia sin sesion.
      headers: { 'Content-Type': 'application/json', ...sesion, ...(opciones.headers || {}) }
    });
  } catch (error) {
    throw new MinutasApiError(
      `No se pudo contactar con la API en ${API}. ¿Está corriendo el backend?`,
      { code: 'NETWORK_ERROR' }
    );
  }

  let datos;
  try {
    datos = await respuesta.json();
  } catch {
    throw new MinutasApiError(`Respuesta no válida del servidor (HTTP ${respuesta.status})`, {
      code: 'BAD_RESPONSE',
      status: respuesta.status
    });
  }

  if (!respuesta.ok || datos.success === false) {
    throw new MinutasApiError(datos.error || `HTTP ${respuesta.status}`, {
      code: datos.code || 'HTTP_ERROR',
      status: respuesta.status,
      providerStatus: datos.providerStatus || null
    });
  }

  return datos;
}

/** Lee un File del navegador como base64 sin la cabecera data:. */
function archivoABase64(file) {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => {
      const resultado = lector.result || '';
      const coma = resultado.indexOf(',');
      resolve(coma >= 0 ? resultado.slice(coma + 1) : resultado);
    };
    lector.onerror = () => reject(new MinutasApiError('No se pudo leer el archivo', { code: 'FILE_READ_ERROR' }));
    lector.readAsDataURL(file);
  });
}

/** Estado del motor y de la base de datos. */
export async function estadoMotor() {
  const { engine, database, message } = await peticion('status', { method: 'GET' });
  return { ...engine, bd: database, mensaje: message };
}

/**
 * El identificador de usuario que espera la API: "<objectId>.<tenantId>".
 * MSAL ya lo entrega con esa forma en `homeAccountId`.
 */
export function userIdDeCuenta(account) {
  if (!account) return null;
  return account.homeAccountId || account.localAccountId || null;
}

// ── Quién puede qué ───────────────────────────────────────────
//
// Las plantillas son comunes a toda la organización: lo que una de ellas diga
// sale en las actas de todo el mundo. Por eso subirlas y compartirlas lo lleva
// un grupo reducido, y quién está en ese grupo lo decide quien administra.
//
// Lo de aquí sirve para ESCONDER lo que no toca, no para impedirlo. Impedirlo
// es cosa del servidor, que comprueba el token: un botón escondido lo encuentra
// cualquiera que mire, y esconderlo sin cerrar la puerta sería pintar la puerta
// de color pared.

/**
 * Lo que puede hacer quien ha entrado.
 *
 * El `userId` que devuelve sale del token verificado, no del navegador. Si
 * alguna vez no coincidiera con el que usa la pantalla, es que algo va mal en
 * serio y merece mirarse.
 */
export async function loQuePuedo() {
  const d = await peticion('me', { method: 'GET' });
  return {
    userId: d.userId,
    nombre: d.name,
    correo: d.email,
    permiso: d.permission,
    puedePlantillas: Boolean(d.canTemplates),
    administra: Boolean(d.canAdminister)
  };
}

/** La lista de quien tiene permiso. Solo contesta a quien administra. */
export async function listarPermisos() {
  const { permissions } = await peticion('permissions', { method: 'GET' });
  return permissions;
}

/** Da permiso a alguien, o le cambia el que tenía. */
export async function darPermiso({ userId, correo, nombre, permiso }) {
  const { permission } = await peticion('permissions', {
    method: 'POST',
    body: JSON.stringify({ userId, email: correo, name: nombre, permission: permiso })
  });
  return permission;
}

/** Se lo quita. */
export async function quitarPermiso(userId) {
  const { removed } = await peticion(`permissions?to=${encodeURIComponent(userId)}`, {
    method: 'DELETE'
  });
  return removed;
}

// ── Formatos guardados ────────────────────────────────────────

/** Formatos visibles para el usuario: los suyos y los públicos. */
export async function listarFormatos(userId) {
  const { formats } = await peticion(`formats?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
  return formats;
}

/** Un formato con su HTML, listo para generar. */
export async function obtenerFormato(formatId, userId) {
  const { format } = await peticion(`formats/${formatId}?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
  return format;
}

/**
 * El .docx original de una plantilla, en base64.
 *
 * Para verla. El listado es liviano a propósito y no trae el documento; esto se
 * pide solo cuando alguien abre una plantilla para mirarla de verdad.
 */
export async function docxDeFormato(formatId, userId) {
  return peticion(`formats/${formatId}/docx?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
}

/** Sube un .docx y lo guarda como formato reutilizable. */
export async function guardarFormato({ file, userId, nombre, indicaciones = null, esPublico = false }) {
  if (!file) throw new MinutasApiError('No se seleccionó ningún archivo', { code: 'INVALID_REQUEST' });
  if (!(file.name || '').toLowerCase().endsWith('.docx')) {
    throw new MinutasApiError('El formato debe ser un archivo .docx', { code: 'INVALID_REQUEST' });
  }

  const contentBase64 = await archivoABase64(file);
  const { format } = await peticion('formats', {
    method: 'POST',
    body: JSON.stringify({
      userId,
      fileName: file.name,
      name: nombre || file.name.replace(/\.docx$/i, ''),
      instructions: indicaciones,
      isPublic: esPublico,
      contentBase64
    })
  });

  return format;
}

/**
 * Cambia el .docx de una plantilla por otro. Solo el dueño puede.
 *
 * El nombre y las indicaciones se conservan: lo que cambia es el papel, no lo
 * que se dijo sobre cómo rellenarlo.
 */
export async function reemplazarDocxDeFormato(formatId, userId, { contentBase64, fileName = null }) {
  const { format } = await peticion(`formats/${formatId}/docx`, {
    method: 'PUT',
    body: JSON.stringify({ userId, contentBase64, fileName })
  });
  return format;
}

/** Cambia nombre, indicaciones o visibilidad. Solo el dueño puede. */
export async function actualizarFormato(formatId, userId, cambios) {
  const { format } = await peticion(`formats/${formatId}`, {
    method: 'PUT',
    body: JSON.stringify({ userId, ...cambios })
  });
  return format;
}

/** Borra un formato propio. */
export async function borrarFormato(formatId, userId) {
  await peticion(`formats/${formatId}?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
  return true;
}

// ── Minutas guardadas ─────────────────────────────────────────

/** Minutas del usuario, más recientes primero. Sin el contenido. */
export async function listarMinutas(userId, limite = 100) {
  const { minutes } = await peticion(
    `minutes?userId=${encodeURIComponent(userId)}&limit=${limite}`, { method: 'GET' }
  );
  return minutes;
}

/** Una minuta con su contenido. */
export async function obtenerMinuta(minuteId, userId) {
  const { minute } = await peticion(
    `minutes/${minuteId}?userId=${encodeURIComponent(userId)}`, { method: 'GET' }
  );
  return minute;
}

/** Guarda una minuta recién generada. Crea la versión 1 del historial. */
export async function guardarMinuta({ userId, titulo, html, transcripcion = null, formatId = null, meetingDate = null, reunionId = null, verificacion = null, meta = null, fuente = null }) {
  const { minute } = await peticion('minutes', {
    method: 'POST',
    body: JSON.stringify({
      userId,
      title: titulo,
      html,
      transcript: transcripcion,
      formatId,
      meetingDate,
      // De qué reunión salió. Sin esto no hay forma de saber que una minuta y
      // una cita del calendario son lo mismo, y acaban listándose por separado.
      recordingId: reunionId,
      // Cómo se creó: 0 Teams, 1 archivo, 2 texto pegado, 3 audio. Lo sabe la
      // pantalla que la crea y nadie más, así que si no se manda aquí se pierde
      // para siempre. Es lo que decide después si el acta se puede borrar.
      ...(fuente != null ? { sourceType: fuente } : {}),
      verification: verificacion,
      meta
    })
  });
  return minute;
}

/** Edita una minuta. La versión anterior queda archivada. */
export async function editarMinuta(
  minuteId, userId,
  { html, titulo = null, nota = null, meta = null, formatId = null, automatico = false }
) {
  const { minute } = await peticion(`minutes/${minuteId}`, {
    method: 'PUT',
    // `meta` lleva el contenido por zona y `formatId` la plantilla sobre la que
    // se armó. Hacen falta los dos para rehacer el .docx al reabrir el acta: sin
    // el meta se reconstruía el documento de antes, deshaciendo la corrección
    // sin avisar; sin el formato no se reconstruía nada y la minuta se quedaba
    // sin manera de volver a Word.
    body: JSON.stringify({ userId, html, title: titulo, note: nota, meta, formatId, automatico })
  });
  return minute;
}

/** Historial de versiones de una minuta. */
/**
 * Transcribe un audio, con sus hablantes separados.
 *
 * El archivo pasa por la API y no va del navegador a Azure: el servicio de voz
 * solo acepta la clave del recurso, y esa clave no puede estar aquí. El porqué
 * completo, en `azure-functions/v2/voz/transcribir.js`.
 */
export async function transcribirAudio(userId, archivo, { locales = null, maxSpeakers = null } = {}) {
  const { transcript } = await peticion('audio/transcribe', {
    method: 'POST',
    body: JSON.stringify({
      userId,
      fileName: archivo.name,
      contentBase64: await archivoABase64(archivo),
      locales,
      maxSpeakers
    })
  });
  return transcript;
}

/** Si este despliegue puede transcribir audio, y con qué límites. */
export async function estadoDeVoz(userId) {
  const { speech } = await peticion(`audio/status?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
  return speech;
}

// ── Las firmas ────────────────────────────────────────────────────────
//
// Las pide esta aplicación, desde el buzón configurado en Configuración →
// Correo. Aquí estaban además las tres funciones del flujo de Power Automate
// —ver, guardar y olvidar su dirección—; se han quitado con sus rutas, que ya
// no existen en el servidor.

export async function listarFirmasDeMinuta(minuteId, userId) {
  return peticion(
    `minutes/${minuteId}/signatures?userId=${encodeURIComponent(userId)}`,
    { method: 'GET' }
  );
}

/**
 * Abre una ronda y avisa a quien le toque.
 *
 * Devuelve `{round, notified, failures}`. `failures` importa y no se puede
 * ignorar: un correo que no sale deja a alguien sin saber que tiene que firmar,
 * y la ronda parada sin que nada lo delate.
 */
export async function mandarAFirmar(minuteId, userId, encargo) {
  return peticion(`minutes/${minuteId}/signatures`, {
    method: 'POST',
    body: JSON.stringify({ userId, ...encargo })
  });
}

/** Cierra la ronda viva, para poder corregir el acta sin que nadie firme. */
export async function cancelarFirmas(minuteId, userId) {
  const { cancelled } = await peticion(
    `minutes/${minuteId}/signatures?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
  return cancelled;
}

/** Vuelve a mandarle el correo a quien no lo recibió. */
export async function reintentarAvisoDeFirma(roundId, userId) {
  return peticion(`signature-rounds/${roundId}/remind`, {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
}

// ── Las tareas que un acta ya mandó a Planner ─────────────────────────
//
// Se guarda el vínculo, no la tarea. Sirve para enseñar el enlace de lo que ya
// existe y para no volver a crearlo.

export async function listarTareasDeMinuta(minuteId, userId) {
  const { tasks } = await peticion(
    `minutes/${minuteId}/tasks?userId=${encodeURIComponent(userId)}`,
    { method: 'GET' }
  );
  return tasks;
}

export async function registrarTareaDeMinuta(minuteId, userId, tarea) {
  const { task } = await peticion(`minutes/${minuteId}/tasks`, {
    method: 'POST',
    body: JSON.stringify({ userId, ...tarea })
  });
  return task;
}

/** Deja de dar por hecho que existe. No borra nada en Planner. */
export async function olvidarTareaDeMinuta(minuteId, userId, taskId) {
  const { forgotten } = await peticion(
    `minutes/${minuteId}/tasks?userId=${encodeURIComponent(userId)}&taskId=${encodeURIComponent(taskId)}`,
    { method: 'DELETE' }
  );
  return forgotten;
}

export async function listarVersiones(minuteId, userId) {
  const { versions } = await peticion(
    `minutes/${minuteId}/versions?userId=${encodeURIComponent(userId)}`, { method: 'GET' }
  );
  return versions;
}

/** Borra una minuta propia. */
/**
 * Borra una minuta, con su historial de versiones y sus rondas de firma.
 *
 * El backend se niega si hay una ronda de firmas abierta (`FIRMAS_EN_CURSO`):
 * ahí hay personas a las que se les pidió firmar, y borrarla les dejaría un
 * enlace muerto sin aviso.
 */
export async function borrarMinuta(minuteId, userId) {
  await peticion(`minutes/${minuteId}?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
  return true;
}

// ── Relleno del Word original ─────────────────────────────────

/**
 * La variante que conserva el formato: se manda la transcripción y el .docx, y
 * vuelve **el mismo documento** con los campos rellenos.
 *
 * A diferencia de `generarMinuta`, el modelo aquí no escribe el documento: solo
 * aporta los valores. El .docx se rellena en su sitio, así que logos,
 * encabezados, fuentes y estilos quedan exactamente como estaban.
 *
 * @param {Object} params
 * @param {File|null} params.file - el .docx a rellenar
 * @param {number|null} params.formatId - o un formato guardado
 * @param {string|Object} params.transcripcion
 * @returns {Promise<Object>} incluye `descargar()` para guardar el archivo
 */
export async function rellenarWord({
  file = null, formatId = null, transcripcion, indicaciones = null,
  userId = null, sinValor = 'No especificado', cita = null
}) {
  // `cita` son los datos duros del calendario: fecha, horario, organizador e
  // invitados. Sin ellos el acta sale con la cabecera en blanco, porque nada de
  // eso se dice en voz alta durante la reunión y no está en la transcripción.
  const cuerpo = { transcript: transcripcion, instructions: indicaciones, sinValor, meeting: cita };

  if (file) {
    if (!(file.name || '').toLowerCase().endsWith('.docx')) {
      throw new MinutasApiError('El formato debe ser un archivo .docx', { code: 'INVALID_REQUEST' });
    }
    cuerpo.contentBase64 = await archivoABase64(file);
    cuerpo.fileName = file.name;
  } else if (formatId != null) {
    cuerpo.formatId = formatId;
    cuerpo.userId = userId;
  } else {
    throw new MinutasApiError('Falta el documento Word', { code: 'INVALID_REQUEST' });
  }

  const datos = await peticion('minute/fill', { method: 'POST', body: JSON.stringify(cuerpo) });
  return actaDevuelta(datos);
}

/**
 * El acta que devuelve el servidor, lista para enseñar y para descargar.
 *
 * La comparten rellenar y re-volcar porque son el mismo objeto visto en dos
 * momentos: recién generado y después de corregirlo a mano. Que tengan la
 * misma forma es lo que permite sustituir uno por otro sin que el resto de la
 * pantalla se entere.
 */
function actaDevuelta(datos) {
  return {
    fileName: datos.fileName,
    bytes: datos.bytes,
    // El documento en HTML, para enseñarlo en pantalla. Es el MISMO archivo que
    // se descarga, no una redacción aparte: por eso lo que se revisa y lo que se
    // envía no pueden separarse.
    html: datos.html || null,
    // El .docx en crudo. Hace falta para subirlo a OneDrive y poder verlo con
    // el aspecto real de Word; `descargar()` no sirve para eso porque entrega
    // el archivo al navegador en vez de devolverlo.
    base64: datos.contentBase64,
    valores: datos.values,
    // Qué es cada zona y, si es tabla, con qué columnas. Es lo que permite
    // ofrecerlas editables una a una en vez de como un bloque de texto.
    regiones: datos.regions || null,
    campos: datos.fields,
    rellenados: datos.filled,
    sinDato: datos.missing,
    uso: datos.usage,
    meta: datos.meta,
    descargar() {
      const binario = atob(datos.contentBase64);
      const bytes = new Uint8Array(binario.length);
      for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: datos.contentType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = datos.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };
}

/**
 * Lee un .docx ya escrito y devuelve su contenido zona por zona.
 *
 * Es el camino de vuelta desde Word: tras editar el acta allí, el documento de
 * OneDrive es la versión buena y la aplicación tiene que ponerse al día con él.
 * No pasa por el modelo —el documento se cuenta a sí mismo— así que no cuesta
 * tokens ni reinterpreta nada de lo que la persona escribió.
 */
export async function leerActa({ contentBase64 }) {
  if (!contentBase64) {
    throw new MinutasApiError('Falta el documento que leer', { code: 'INVALID_REQUEST' });
  }
  const datos = await peticion('minute/read', {
    method: 'POST',
    body: JSON.stringify({ contentBase64 })
  });
  return {
    html: datos.html || null,
    valores: datos.values,
    regiones: datos.regions || null,
    campos: datos.fields,
    meta: datos.meta
  };
}

/**
 * Vuelve a volcar el acta con las zonas corregidas a mano.
 *
 * No pasa por el modelo: lo que se manda es lo que se escribe. Volver a
 * generar devolvería un acta distinta de la que se acaba de revisar, y el
 * trabajo de corrección se perdería.
 */
export async function reaplicarWord({
  file = null, formatId = null, valores, userId = null, minuteId = null
}) {
  if (!valores || typeof valores !== 'object') {
    throw new MinutasApiError('Faltan los valores por zona', { code: 'INVALID_REQUEST' });
  }

  const cuerpo = { values: valores };

  // Con el número del acta, el servidor vuelve a estampar las rúbricas ya
  // dibujadas. Hace falta porque el documento se rehace desde la plantilla en
  // cada guardado y una imagen no sale de `values`: sin esto, la firma de quien
  // ya había firmado se borraba al corregir cualquier campo.
  if (minuteId != null) cuerpo.minuteId = minuteId;

  if (file) {
    cuerpo.contentBase64 = await archivoABase64(file);
    cuerpo.fileName = file.name;
  } else if (formatId != null) {
    cuerpo.formatId = formatId;
    cuerpo.userId = userId;
  } else {
    throw new MinutasApiError('Falta el documento Word', { code: 'INVALID_REQUEST' });
  }

  const datos = await peticion('minute/refill', { method: 'POST', body: JSON.stringify(cuerpo) });
  return actaDevuelta(datos);
}

/**
 * Lo que se puede rellenar en un .docx, sin rellenarlo.
 *
 * Devuelve las dos cosas porque hay dos maneras de rellenar un acta y son
 * excluyentes: los `campos` son marcadores puestos a mano (`{{Campo}}`), y las
 * `regiones` son los huecos deducidos de la estructura del documento cuando no
 * hay marcadores —que es lo normal, porque lo que la gente sube es el acta de
 * la reunión anterior.
 */
export async function camposDelWord(file) {
  const contentBase64 = await archivoABase64(file);
  const { fields, regions } = await peticion('format/fields', {
    method: 'POST',
    body: JSON.stringify({ contentBase64, fileName: file.name })
  });
  return { campos: fields || [], regiones: regions || [] };
}

// ── Preferencias de la persona ────────────────────────────────

/**
 * El aspecto elegido por el usuario: 'claro', 'oscuro' o 'sistema'.
 *
 * Devuelve `{tema: null}` cuando nunca ha elegido, y también cuando la columna
 * no existe todavía. Quien llama no debe distinguir esos dos casos: en ambos lo
 * correcto es seguir al sistema.
 */
export async function obtenerPreferencias(userId) {
  const { preferences } = await peticion(
    `preferences?userId=${encodeURIComponent(userId)}`, { method: 'GET' }
  );
  return preferences;
}

/** Guarda el aspecto para que acompañe a la persona a otro equipo. */
export async function guardarPreferencias(userId, { tema }) {
  const { preferences } = await peticion('preferences', {
    method: 'PUT',
    body: JSON.stringify({ userId, theme: tema })
  });
  return preferences;
}

// ── Catálogos que se llenan solos ─────────────────────────────

/**
 * Los valores que esta persona ya ha usado en un campo del acta.
 *
 * Nunca revienta: un catálogo vacío deja el campo como texto libre, que es lo
 * que era antes de que existiera. Una sugerencia que no llega no puede impedir
 * escribir un acta.
 */
export async function listarCatalogo(userId, kind) {
  try {
    const { values } = await peticion(
      `catalog?kind=${encodeURIComponent(kind)}&userId=${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );
    return values || [];
  } catch {
    return [];
  }
}

/** Apunta un valor recién usado, para ofrecerlo la próxima vez. */
export async function registrarEnCatalogo(userId, kind, valor) {
  try {
    const { value } = await peticion('catalog', {
      method: 'POST',
      body: JSON.stringify({ userId, kind, value: valor })
    });
    return value;
  } catch {
    return null;
  }
}

/**
 * Cambia el nombre de un valor del catálogo.
 *
 * Este sí puede fallar hacia arriba, al revés que listar y registrar: quien lo
 * llama acaba de pulsar «Guardar» en un formulario y tiene que enterarse si no
 * se guardó. Los otros dos ocurren de fondo, sin que nadie los pida.
 */
export async function renombrarEnCatalogo(userId, kind, antes, despues) {
  const { value } = await peticion('catalog', {
    method: 'PUT',
    body: JSON.stringify({ userId, kind, from: antes, to: despues })
  });
  return value;
}

/** Quita del catálogo algo que se coló mal escrito. */
export async function olvidarDelCatalogo(userId, kind, valor) {
  const url = `catalog?kind=${encodeURIComponent(kind)}`
    + `&value=${encodeURIComponent(valor)}&userId=${encodeURIComponent(userId)}`;
  try {
    await peticion(url, { method: 'DELETE' });
    return true;
  } catch {
    return false;
  }
}

// ── Preguntas sobre la reunión ────────────────────────────────

/**
 * Pregunta libre sobre una reunión, respondida solo con su transcripción.
 *
 * La transcripción se puede mandar entera o, si la minuta ya está guardada,
 * referirla con `minuteId`: en un hilo de varias preguntas, reenviar 200 KB de
 * transcripción en cada una no tiene sentido.
 *
 * @param {Object} params
 * @param {string} params.pregunta
 * @param {string|Object|null} params.transcripcion
 * @param {number|null} params.minuteId
 * @param {Array<{role: string, content: string}>} params.historial
 * @returns {Promise<{respuesta: string, uso: Object, meta: Object}>}
 */
export async function preguntarSobreReunion({ pregunta, transcripcion = null, minuteId = null, userId = null, historial = [], minutaHtml = null }) {
  const datos = await peticion('minute/ask', {
    method: 'POST',
    body: JSON.stringify({
      question: pregunta,
      history: historial,
      minuteHtml: minutaHtml,
      ...(transcripcion != null ? { transcript: transcripcion } : { minuteId, userId })
    })
  });

  return { respuesta: datos.answer, uso: datos.usage, meta: datos.meta };
}

// ── Exportación a Word ────────────────────────────────────────

/**
 * Convierte la minuta en un .docx y lo descarga.
 *
 * El backend devuelve el documento en base64: el host de Functions no
 * transporta binarios de forma fiable por la ruta comodín, y reconstruir el
 * Blob aquí es trivial.
 *
 * @param {Object} params
 * @param {string} params.html - la minuta
 * @param {string} params.titulo - encabezado y nombre de archivo
 */
export async function descargarComoWord({ html, titulo = null, minuteId = null, userId = null }) {
  const datos = await peticion('export/docx', {
    method: 'POST',
    body: JSON.stringify({
      ...(minuteId != null ? { minuteId, userId } : { html }),
      title: titulo
    })
  });

  const binario = atob(datos.contentBase64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);

  const url = URL.createObjectURL(new Blob([bytes], { type: datos.contentType }));
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = datos.fileName;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  URL.revokeObjectURL(url);

  return { fileName: datos.fileName, bytes: datos.bytes };
}

// ── Importación de plantillas v1 ──────────────────────────────

/** Plantillas de v1 visibles, marcando cuáles ya se importaron. */
export async function listarPlantillasV1(userId) {
  const { templates } = await peticion(`templates/v1?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
  return templates;
}

/**
 * Importa plantillas v1 como formatos. No modifica la tabla Templates: v1 sigue
 * funcionando igual. Repetirlo solo trae lo que falte.
 *
 * @param {string} userId
 * @param {Array<number>|null} templateIds - null para importar todas las pendientes
 */
export async function importarPlantillasV1(userId, templateIds = null) {
  return peticion('templates/import', {
    method: 'POST',
    body: JSON.stringify({ userId, ...(templateIds ? { templateIds } : {}) })
  });
}

/**
 * Sube un .docx y devuelve el Formato (secciones, placeholders, HTML).
 * @param {File} file
 */
export async function parsearFormato(file) {
  if (!file) throw new MinutasApiError('No se seleccionó ningún archivo', { code: 'INVALID_REQUEST' });

  const nombre = file.name || '';
  if (!nombre.toLowerCase().endsWith('.docx')) {
    throw new MinutasApiError('El formato debe ser un archivo .docx', { code: 'INVALID_REQUEST' });
  }

  const contentBase64 = await archivoABase64(file);
  const { format } = await peticion('format/parse', {
    method: 'POST',
    body: JSON.stringify({ fileName: nombre, contentBase64 })
  });

  return format;
}

/**
 * El contrato central de la v2: se manda la transcripción y el formato.
 *
 * El formato puede ir de dos maneras: `formatId` si está guardado, o `formato`
 * si se acaba de parsear y aún no se ha guardado.
 *
 * @param {Object} params
 * @param {string|Object|Array} params.transcripcion - texto, array de turnos o {phrases:[...]}
 * @param {number|null} params.formatId - id de un formato guardado
 * @param {Object|null} params.formato - Formato devuelto por parsearFormato()
 * @param {string|null} params.indicaciones - instrucciones adicionales
 * @param {string|null} params.userId - necesario si se usa formatId
 * @returns {Promise<{html: string, verificacion: Object, uso: Object, meta: Object}>}
 */
export async function generarMinuta({ transcripcion, formatId = null, formato = null, indicaciones = null, userId = null }) {
  const datos = await peticion('minute/generate', {
    method: 'POST',
    body: JSON.stringify({
      transcript: transcripcion,
      // `formatId` tiene precedencia en el backend; se manda solo uno de los dos.
      ...(formatId != null ? { formatId, userId } : { format: formato }),
      instructions: indicaciones
    })
  });

  return {
    html: datos.html,
    verificacion: datos.verification,
    uso: datos.usage,
    meta: datos.meta
  };
}

const minutasV2Service = {
  estadoMotor,
  userIdDeCuenta,
  usarTokenDeSesion,
  loQuePuedo,
  listarPermisos,
  darPermiso,
  quitarPermiso,
  parsearFormato,
  listarFormatos,
  obtenerFormato,
  guardarFormato,
  actualizarFormato,
  borrarFormato,
  listarPlantillasV1,
  importarPlantillasV1,
  descargarComoWord,
  listarMinutas,
  obtenerMinuta,
  guardarMinuta,
  editarMinuta,
  listarVersiones,
  borrarMinuta,
  rellenarWord,
  reaplicarWord,
  leerActa,
  preguntarSobreReunion,
  obtenerPreferencias,
  guardarPreferencias,
  listarCatalogo,
  registrarEnCatalogo,
  renombrarEnCatalogo,
  olvidarDelCatalogo,
  camposDelWord,
  generarMinuta,
  MinutasApiError
};
export default minutasV2Service;

// ── El buzón desde el que salen los correos ────────────────────────────
//
// La contraseña viaja SOLO de aquí hacia el servidor y no vuelve nunca: lo que
// llega de vuelta trae `tienePassword`, que es lo único que hace falta para
// saber si está puesta. Por eso `editarPerfilDeCorreo` manda la contraseña
// solo cuando se ha escrito una nueva: si la mandara siempre, mandaría vacío
// —que es lo que la pantalla tiene— y la borraría.

export async function listarPerfilesDeCorreo(userId) {
  return peticion(`mail-profiles?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
}

export async function crearPerfilDeCorreo(userId, datos) {
  const { profile } = await peticion('mail-profiles', {
    method: 'POST',
    body: JSON.stringify({ userId, ...datos })
  });
  return profile;
}

export async function editarPerfilDeCorreo(userId, id, cambios) {
  const { profile } = await peticion(`mail-profiles/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ userId, ...cambios })
  });
  return profile;
}

export async function borrarPerfilDeCorreo(userId, id) {
  const { deleted } = await peticion(
    `mail-profiles/${id}?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
  return deleted;
}

export async function marcarPerfilPrincipal(userId, id) {
  const { profile } = await peticion(`mail-profiles/${id}/default`, {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
  return profile;
}

/** Manda un correo de prueba. Devuelve `{sent, to, from}` o `{sent:false, error}`. */
export async function probarPerfilDeCorreo(userId, id, para = null) {
  return peticion(`mail-profiles/${id}/test`, {
    method: 'POST',
    body: JSON.stringify({ userId, para })
  });
}

// ── La página de firma ─────────────────────────────────────────────────
//
// Estas dos NO mandan `userId`: las llama quien recibió el correo, que puede no
// haber abierto nunca esta aplicación. El token dice QUÉ se firma; el correo de
// quien ha entrado, que va en `asEmail`, dice QUIÉN firma. El servidor
// comprueba que coincidan: un enlace reenviado no convierte a nadie en firmante.

export async function verLoQueSeFirma(token) {
  const { signature } = await peticion(`sign/${encodeURIComponent(token)}`, { method: 'GET' });
  return signature;
}

/**
 * La dirección del acta en PDF de una solicitud de firma.
 *
 * Es una URL y no una descarga porque el destino es un `<a>` o un `<iframe>`:
 * traerse el archivo por `fetch` para volver a soltarlo obligaría a tener el
 * acta entera en memoria sin ganar nada. El servidor la manda con
 * `Content-Disposition: inline`, así que el navegador la enseña.
 */
export function urlDelActaEnPdf(token) {
  return `${API}/v2/sign/${encodeURIComponent(token)}/document`;
}

export async function firmarActa(token, { aprueba, observaciones = null, firma = null, comoCorreo }) {
  return peticion(`sign/${encodeURIComponent(token)}`, {
    method: 'POST',
    // `signature` es la rubrica dibujada, en PNG base64 y sin la cabecera
    // `data:`. Es opcional: quien no dibuja firma igual.
    body: JSON.stringify({
      approve: aprueba, comments: observaciones, asEmail: comoCorreo,
      ...(firma ? { signature: firma } : {})
    })
  });
}

/**
 * El acta en Word con las rubricas dibujadas puestas en su sitio.
 *
 * Se rehace al pedirlo desde la plantilla y el contenido por zona, en vez de
 * guardarse firmada: el acta sigue viva mientras se firma, y un documento
 * congelado empezaria a diferir del de verdad sin que nada lo dijera.
 */
export async function wordFirmado(minuteId, userId) {
  return peticion(`minutes/${minuteId}/signed-docx?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
}
